import _ from 'underscore';

import BaseController from 'app/src/controllers/base/BaseController';
import Localize from 'app/src/localize/Localize';
import ButtonView from 'app/src/views/components/ButtonView';

const _ButtonType = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  DANGER: 'danger',
  UTILITY: 'utility',
};

export default class ButtonViewController extends BaseController {
  static get ButtonType() {
    return _ButtonType;
  }

  /**
   * Constructor for a ButtonViewController.  A ButtonViewController provides asynchronous behaviours
   * to a button control used in a DetailView.
   *
   * @param {Object} options  Options to configure the button
   * @param {string} options.name     The internally defined name of the button
   * @param {string} options.type     The type of button (either 'primary', 'secondary' or 'danger'.
   * @param {string} options.text     The text that will appear for the button in it's ready state
   * @param {string} options.inProgressText   (optional)  When present, this text will be used while
   *                                          the button's action is in progress.
   * @param {string} options.doneMessage      (optional) When present, this text will be shown for
   *                                          one second after the button's action has finished successfully.
   */
  constructor(options = {}) {
    super();

    this._options = options;

    this.view = new ButtonView();
  }

  /**
   * Retrieves the name that was assigned to the button (via the options)
   *
   * @returns {string}    The name of the button
   */
  get name() {
    return this._options.name;
  }

  /**
   * Shows the button.  Adds configurable items to the buttons properties (id, href, etc)
   * based on the button's configuration.
   */
  show() {
    super.show();

    let type = this._options.type || _ButtonType.PRIMARY;
    let name = this._options.name || 'button';

    this.view.$el.addClass(`btn-${type}`);
    if (this._options.type === _ButtonType.DANGER) {
      this.view.$el.addClass('right');
    }

    this.view.$el.prop('href', `#${name}`);
    this.view.$el.prop('id', `${name}_button`);
    this.view.setButtonText(Localize.translate(this._options.text || 'Button'));
  }

  /**
   * Event handler for when the button is clicked.  Invokes the action.
   */
  onButtonClicked() {
    this._doAction();
  }

  /**
   * Executes the button's action if an actionDelegate is provided via the options.  If no
   * delegate is provided, then the button will have no behaviour.  The provided delegate
   * is expected to return a promise which is resolved when the action successfully completes
   * and is rejected when it fails to complete.
   *
   * @private
   */
  _doAction() {
    if (this._options.actionDelegate && _.isFunction(this._options.actionDelegate)) {
      let buttonText = Localize.translate(this._options.text || 'Button');
      let inProgressText = Localize.translate(this._options.inProgressText || null);
      let doneMessage = Localize.translate(this._options.doneMessage || null);

      if (inProgressText) {
        this.view.setButtonText(inProgressText);
        this.view.setState(ButtonView.ButtonState.IN_PROGRESS);
      }

      this._options
        .actionDelegate()
        .done(() => {
          if (this.view) {
            if (doneMessage) {
              this.view.setState(ButtonView.ButtonState.DONE);
              this.view.setButtonText(doneMessage);

              setTimeout(() => {
                // we don't store the timer ID (or try to clean it up) so just ignore
                // the timeout if the view has already been destroyed.
                if (this.view) {
                  this.view.setState(ButtonView.ButtonState.READY);
                  this.view.setButtonText(buttonText);
                }
              }, 1000);
            } else {
              this.view.setState(ButtonView.ButtonState.READY);
              this.view.setButtonText(buttonText);
            }
          }
        })
        .fail(() => {
          if (this.view) {
            this.view.setState(ButtonView.ButtonState.READY);
            this.view.setButtonText(buttonText);
          }
        });
    }
  }
}
